import { Component, computed, DestroyRef, effect, inject, Injector, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatChip, MatChipRemove } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslateService } from '@ngx-translate/core';
import { Subject, take } from 'rxjs';
import { ESqlOperators, TFilterSave } from 'scw-digital-boards-widgets';
import { LineAdvancedFilterApiMatcher } from '../advanced-filter-api-matcher/line-advanced-filter-api-matcher';
import { IFilterIdentity, ISelectedApiDropdown, TFilterSaveWithOnHold } from '../advanced-filter.interface';
import { AdvancedFilterService } from '../advanced-filter.service';
import {
  IShiftFilterDialogData,
  ShiftFilterDialogComponent,
} from '../shift-filter-dialog/shift-filter-dialog.component';

@Component({
  imports: [MatChip, MatChipRemove, MatIcon],
  providers: [AdvancedFilterService],
  selector: 'app-shift-phrase-filter-chip',
  styleUrl: './shift-phrase-filter-chip.component.scss',
  templateUrl: './shift-phrase-filter-chip.component.html',
})
export class ShiftPhraseFilterChipComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly matDialog = inject(MatDialog);
  private readonly advancedFilterService = inject(AdvancedFilterService);

  public readonly dateFilter = input.required<ISelectedApiDropdown>();
  public readonly shiftFilter = input.required<ISelectedApiDropdown>();
  public readonly apiFilters = input.required<Record<string, number>>();
  public readonly oneEditOperationAtATimeSubject = input<Subject<symbol> | null>(null);
  public readonly removeClick = output<void>();
  public readonly addFilter = output<TFilterSaveWithOnHold>();
  public readonly hoveredFilterIdentityUpdate = output<IFilterIdentity | null>();

  private readonly dateFilter$ = toObservable(this.dateFilter);
  private matcher: LineAdvancedFilterApiMatcher | null = null;
  private readonly currentShiftText = this.translate.instant('general.currentShift');
  private readonly previousShiftText = this.translate.instant('general.previousShift');

  public readonly label = computed(() => {
    const shiftText =
      this.dateFilter().operator === ESqlOperators.$currentShift ? this.currentShiftText : this.previousShiftText;

    return this.matcher ? `${shiftText} (${this.matcher.label()})` : shiftText;
  });

  constructor() {
    effect(() => {
      const dateFilter = this.dateFilter();
      const shiftFilter = this.shiftFilter();
      const isInvalid = this.matcher?.isInvalid() ?? null;

      dateFilter.isInvalid.set(isInvalid);
      shiftFilter.isInvalid.set(isInvalid);
    });
  }

  public ngOnInit(): void {
    this.dateFilter$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.matcher) {
        this.matcher = new LineAdvancedFilterApiMatcher(this.dateFilter().value, this.apiFilters, this.injector);
      }
    });
  }

  public onChipClick(): void {
    this.oneEditOperationAtATimeSubject()?.next(Symbol());

    this.matDialog
      .open(ShiftFilterDialogComponent, {
        ...ShiftFilterDialogComponent.dialogConfig,
        data: {
          lineId: this.dateFilter().value,
          mode: this.dateFilter().operator === ESqlOperators.$currentShift ? 'currentShift' : 'previousShift',
        } satisfies IShiftFilterDialogData,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((filters?: TFilterSave[]) => {
        if (!filters?.length) {
          return;
        }

        const editedFilterIds = [this.dateFilter().id, this.shiftFilter().id];

        for (const filter of this.advancedFilterService.replaceShiftPhraseGroup(editedFilterIds, filters)) {
          this.addFilter.emit(filter);
        }
      });
  }
}
